import { useEffect, useRef, type JSX } from 'react'
import { useEventVariables } from '../../../hooks/useEventVariables.js'

export type Channel = 'email' | 'sms' | 'in_app' | 'push'

export interface TemplateEditorProps {
  /** Subject template; `null` when the channel set has no subject channel. */
  subjectTemplate: string | null
  /** Body template — the primary content, edited in the textarea. */
  bodyTemplate: string
  /** Channels selected for this template; drives subject visibility. */
  channels: Channel[]
  /** Who receives the notification. */
  audience: 'agent' | 'customer'
  /** Notification event key — drives the variable picker catalog. */
  eventKey: string
  /**
   * Reports edits back to the controlling parent. Fires on subject/body input,
   * variable insertion, and the subject→null transition when the channel set
   * stops carrying a subject. May be passed unmemoized; the component holds it
   * in a ref so the transition effect never loops.
   */
  onChange: (next: {
    subjectTemplate: string | null
    bodyTemplate: string
  }) => void
}

/** Channels that carry a subject line. */
const SUBJECT_CHANNELS: ReadonlySet<Channel> = new Set(['email', 'push'])

/** True when any selected channel renders a subject. */
function hasSubjectChannel(channels: Channel[]): boolean {
  return channels.some((c) => SUBJECT_CHANNELS.has(c))
}

/**
 * Controlled editor for a notification template: a subject input (shown only
 * for email/push channels), a body textarea, and a variable picker panel.
 * Clicking a variable splices `{{name}}` into the body at the caret position.
 * Data fetching is limited to {@link useEventVariables} for the picker list;
 * the parent owns persistence and composes the LivePreview alongside it.
 */
export default function TemplateEditor({
  subjectTemplate,
  bodyTemplate,
  channels,
  audience,
  eventKey,
  onChange,
}: TemplateEditorProps): JSX.Element {
  const { variables, loading, error } = useEventVariables(eventKey)
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  const showSubject = hasSubjectChannel(channels)

  // Hold the latest `onChange` in a ref so the transition effect below does not
  // depend on it. A parent passing a fresh `onChange` each render would
  // otherwise re-run that effect every render and risk an infinite loop.
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  })

  // When the channel set no longer carries a subject, the contract says
  // `subjectTemplate` must collapse to null. Emit that transition once.
  useEffect(() => {
    if (!showSubject && subjectTemplate !== null) {
      onChangeRef.current({ subjectTemplate: null, bodyTemplate })
    }
  }, [showSubject, subjectTemplate, bodyTemplate])

  const bodyRequired = bodyTemplate.trim() === ''

  function handleSubjectChange(value: string): void {
    onChange({ subjectTemplate: value, bodyTemplate })
  }

  function handleBodyChange(value: string): void {
    onChange({ subjectTemplate, bodyTemplate: value })
  }

  function insertVariable(name: string): void {
    const token = `{{${name}}}`
    const el = bodyRef.current
    const start = el?.selectionStart ?? bodyTemplate.length
    const end = el?.selectionEnd ?? bodyTemplate.length
    const next = bodyTemplate.slice(0, start) + token + bodyTemplate.slice(end)
    onChange({ subjectTemplate, bodyTemplate: next })
    // Restore the caret just after the inserted token.
    if (el) {
      const caret = start + token.length
      requestAnimationFrame(() => {
        el.focus()
        el.setSelectionRange(caret, caret)
      })
    }
  }

  return (
    <section
      aria-label="Template editor"
      className="te flex flex-col gap-3 md:flex-row"
    >
      <div className="flex flex-1 flex-col gap-3">
        {showSubject && (
          <div className="flex flex-col gap-1">
            <label
              className="text-xs font-medium text-gray-600"
              htmlFor="te-subject"
            >
              Subject
            </label>
            <input
              id="te-subject"
              type="text"
              className="rounded border border-gray-300 p-2 text-sm"
              value={subjectTemplate ?? ''}
              onChange={(e) => handleSubjectChange(e.target.value)}
            />
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label
            className="text-xs font-medium text-gray-600"
            htmlFor="te-body"
          >
            Body
          </label>
          <textarea
            id="te-body"
            ref={bodyRef}
            className="h-48 rounded border border-gray-300 p-2 font-mono text-sm"
            aria-invalid={bodyRequired}
            aria-describedby={bodyRequired ? 'te-body-error' : undefined}
            value={bodyTemplate}
            onChange={(e) => handleBodyChange(e.target.value)}
          />
          {bodyRequired && (
            <p
              id="te-body-error"
              role="alert"
              className="text-xs text-red-600"
            >
              Body is required.
            </p>
          )}
        </div>
      </div>

      <aside
        aria-label="Variable picker"
        className="flex w-full flex-col gap-1 rounded border border-gray-300 p-3 md:w-56"
      >
        <h3 className="text-sm font-semibold">
          Variables
          <span className="ml-1 font-normal text-gray-500">({audience})</span>
        </h3>
        {loading && <p className="text-xs text-gray-400">Loading…</p>}
        {error !== null && (
          <p role="alert" className="text-xs text-red-600">
            {error}
          </p>
        )}
        {!loading && error === null && variables.length === 0 && (
          <p className="text-xs text-gray-400">No variables available.</p>
        )}
        <ul className="flex flex-col gap-1">
          {variables.map((v) => (
            <li key={v.name}>
              <button
                type="button"
                className="w-full rounded px-2 py-1 text-left text-xs hover:bg-gray-100"
                aria-label={`Insert ${v.label}`}
                title={`Example: ${v.example}`}
                onClick={() => insertVariable(v.name)}
              >
                <span className="font-medium">{v.label}</span>
                <span className="block font-mono text-gray-500">
                  {`{{${v.name}}}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>
    </section>
  )
}
